import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './IvMeasurementLineChart.css';
import '../../App.css';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSVLink } from 'react-csv';
import { ko } from 'date-fns/locale';

import config from '../../config';

// 시간 범위 내에 데이터가 있는지 확인하는 함수 (변환 없이 그대로 사용)
const isWithinTimeRange = (createdAt, targetTime) => {
  const [targetHours, targetMinutes] = targetTime.split(':').map(Number);
  const dateObj = new Date(createdAt);

  const itemHours = dateObj.getUTCHours();
  const itemMinutes = dateObj.getUTCMinutes();

  const startTime = targetHours * 60 + targetMinutes;
  const endTime = startTime + 10;
  const itemTimeMinutes = itemHours * 60 + itemMinutes;

  return itemTimeMinutes >= startTime && itemTimeMinutes < endTime;
};

// 가장 가까운 데이터를 찾고 없으면 0을 반환하는 함수
const findClosestTimeData = (filteredData, targetTime) => {
  const closestData = filteredData.find(item => isWithinTimeRange(item.created_at, targetTime));

  if (!closestData) {
    return { value: 0, item: null };
  }

  return { value: closestData.Grid_Power, item: closestData };
};

const IvMeasurementLineChart = ({ SelInverter}) => {
  
  const [chartData, setChartData] = useState(new Array(90).fill(0));
  const [tooltipData, setTooltipData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // timeLabels를 useMemo로 메모이제이션
  const timeLabels = useMemo(() => [
    '05:00', '05:10', '05:20', '05:30', '05:40', '05:50',
    '06:00', '06:10', '06:20', '06:30', '06:40', '06:50',
    '07:00', '07:10', '07:20', '07:30', '07:40', '07:50',
    '08:00', '08:10', '08:20', '08:30', '08:40', '08:50',
    '09:00', '09:10', '09:20', '09:30', '09:40', '09:50',
    '10:00', '10:10', '10:20', '10:30', '10:40', '10:50',
    '11:00', '11:10', '11:20', '11:30', '11:40', '11:50',
    '12:00', '12:10', '12:20', '12:30', '12:40', '12:50',
    '13:00', '13:10', '13:20', '13:30', '13:40', '13:50',
    '14:00', '14:10', '14:20', '14:30', '14:40', '14:50',
    '15:00', '15:10', '15:20', '15:30', '15:40', '15:50',
    '16:00', '16:10', '16:20', '16:30', '16:40', '16:50',
    '17:00', '17:10', '17:20', '17:30', '17:40', '17:50',
    '18:00', '18:10', '18:20', '18:30', '18:40', '18:50',
    '19:00', '19:10', '19:20', '19:30', '19:40', '19:50',
    '20:00'
  ], []);

  const fetchData = useCallback(async () => {
    try {
      // 선택된 날짜를 기준으로 KST 시간대를 고려한 문자열 생성
      const selectedDateInKST = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];
      const response = await fetch(`${config.backendUrl}/get_hourly_generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: selectedDateInKST, // 변환된 KST 날짜를 서버에 전달
          seq: SelInverter,
        }),
      });

      const result = await response.json();
      // console.log('✅ 백엔드에서 받아온 result:', result);
      const filtered = result.data.filter(item => item.seq === SelInverter);

      // 차트에 넣을 데이터/ 툴팁에 넣을 데이터 배열 선언
      const chartDataByTime = [];
      const tooltipDataByTime = [];

      timeLabels.forEach(label => {
        const { value, item } = findClosestTimeData(filtered, label);
        chartDataByTime.push(value);
        tooltipDataByTime.push(item);
      });

      setChartData(chartDataByTime);
      setTooltipData(tooltipDataByTime);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [SelInverter, timeLabels, selectedDate]);

  useEffect(() => {
    if (SelInverter) {
      fetchData();
      const interval = setInterval(fetchData, 10 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [SelInverter, fetchData]);

  const generateCSVData = () => {
    return timeLabels.map((label, index) => ({
      Time: label,
      GridPower: chartData[index],
      created_at: tooltipData[index]?.created_at || 'N/A',
      PvTotalVolt: tooltipData[index]?.PvTotalVolt || 'N/A',
      PvTotalCurrent: tooltipData[index]?.PvTotalCurrent || 'N/A',
      PvPower: tooltipData[index]?.PvPower || 'N/A',
      Grid_RS_Volt: tooltipData[index]?.Grid_RS_Volt || 'N/A',
      Grid_ST_Volt: tooltipData[index]?.Grid_ST_Volt || 'N/A',
      Grid_TR_Volt: tooltipData[index]?.Grid_TR_Volt || 'N/A'
    }));
  };

  const csvHeaders = [
    { label: 'Time', key: 'Time' },
    { label: 'Grid Power (kW)', key: 'GridPower' },
    { label: 'Time of Data', key: 'created_at' },
    { label: 'PvTotalVolt', key: 'PvTotalVolt' },
    { label: 'PvTotalCurrent', key: 'PvTotalCurrent' },
    { label: 'PvPower', key: 'PvPower' },
    { label: 'Grid_RS_Volt', key: 'Grid_RS_Volt' },
    { label: 'Grid_ST_Volt', key: 'Grid_ST_Volt' },
    { label: 'Grid_TR_Volt', key: 'Grid_TR_Volt' }
  ];

  const data = {
    labels: timeLabels,
    datasets: [
      {
        data: chartData,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
        maxBarThickness: 20,
      }
    ]
  };

  const options = {
    scales: {
      y: {
        title: {
          display: true,
          text: '인버터출력 (kW)',
        },
        beginAtZero: true,
        ticks: {
          precision: 1,
          stepSize: (context) => {
            const maxVal = Math.max(...chartData.map(val => val / 1000));
            return maxVal <= 1 ? 0.1 : Math.ceil(maxVal);
          },
          callback: function(value) {
            return (value/1000).toFixed(1);
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(tooltipItem) {
            const index = tooltipItem.dataIndex;
            const item = tooltipData[index];
            return item ? [
              `Grid Power: ${(item.Grid_Power / 1000).toFixed(3)} KW`,
              `Time: ${item.created_at}`,
              `PvTotalVolt: ${(item.PvTotalVolt / 10).toFixed(1)} V`,
              `PvTotalCurrent: ${(item.PvTotalCurrent / 10).toFixed(1)} A`,
              `PvPower: ${(item.PvPower / 1000).toFixed(3)} KW`,
              `Grid_RS_Volt: ${(item.Grid_RS_Volt / 10).toFixed(1)} V`,
              `Grid_ST_Volt: ${(item.Grid_ST_Volt / 10).toFixed(1)} V`,
              `Grid_TR_Volt: ${(item.Grid_TR_Volt / 10).toFixed(1)} V`
            ] : 'No Data';
          }
        }
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 20,
    },
    backgroundColor: '#ffffff'
  };

  return (
    <div className="BarChart-Container">
      {/* 날짜 선택기 & CSV 다운로드 */}
      <div className="datepicker-container">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="yyyy-MM-dd"
          maxDate={new Date()}
          className="styled-datepicker"
          locale={ko}
          customInput={<input className="datepicker-input" />}
        />
        
        <CSVLink
          data={generateCSVData()}
          headers={csvHeaders}
          filename={`iv_data_${selectedDate.toISOString().split('T')[0]}.csv`}
          className="styled-button csv-download-button"
          style={{backgroundColor:" #1D6C41"}}
        >
          CSV 다운로드
        </CSVLink>
      </div>

      {/* 차트 영역 */}
      <div style={{ flex: 1, width: "1000px", height: "300px" }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default IvMeasurementLineChart;
